/**
 * Cálculos de boleta interna (control).
 * Se ejecuta DESPUÉS del motor PLAME cuando hay planilla alternativa.
 *
 * Reglas de aislamiento PLAME ↔ interna:
 * - Ingresos/descuentos puestos en interna quedan solo en interna.
 * - Ingresos/descuentos puestos en PLAME quedan solo en PLAME.
 * - Únicos compartidos (copia PLAME → interna del motor): AFP/ONP, EsSalud y minera.
 *
 * Otras reglas:
 * - Genera ingresos sobre sueldo interno (0121 ya prorrateado desde planillaCalculoService).
 * - Vacaciones (0118), feriados/descansos (0107), subsidio (0916) y lic. goce (0907)
 *   se recalculan sobre sueldo_interno y llegan en ingresosInternos.
 * - Tardanzas (0704) e inasistencias/faltas/permisos (0705) se recalculan sobre
 *   sueldo_interno y llegan en descuentosInternos.
 * - Ingresos/descuentos de tareo o maestro con tipo_boleta=interna se suman aquí.
 * - Si es solo_interna: ingresos + descuentos/adelantos internos (sin aportes pensionarios).
 */

function encontrarConceptoPlame(conceptosPlame = [], codigo) {
  const cod = String(codigo);
  return (
    (conceptosPlame || []).find(
      (c) => String(c.codigo) === cod || String(c.codigo_plame) === cod
    ) || null
  );
}

function clasificarRegimenSimple(regimenNombre) {
  const nombre = String(regimenNombre || '').toLowerCase();
  if (nombre.includes('micro')) return 'mype_micro';
  if (nombre.includes('pequeña') || nombre.includes('pequena')) return 'mype_pequena';
  if (nombre.includes('agrario')) return 'agrario';
  if (nombre.includes('formación') || nombre.includes('formacion')) return 'formacion';
  if (nombre.includes('minero')) return 'minero';
  return 'general';
}

/**
 * Whitelist: lo único que se copia del motor PLAME a la boleta interna.
 * AFP: 0601 comisión, 0606 prima, 0608 aportación.
 * ONP: 0607.
 * Minera: 0611 (+2% trab.), 0616 (FCJMUS), 0809 (+2% empleador).
 * EsSalud: 0804.
 * No se copian renta 5ta (0605), SCTR/seguros ni operativos 07xx.
 */
const CODIGOS_COMPARTIDOS_PLAME_INTERNA = new Set([
  '0601', '0606', '0608', // AFP
  '0607',                 // ONP
  '0611', '0616', '0809', // minera
  '0804',                 // EsSalud
]);

/** Descuentos pensionarios/minera que el flag "omitir" debe sacar de la boleta interna. */
const CODIGOS_DESCUENTOS_OMITIR_PLAME_INTERNA = new Set([
  '0601', '0606', '0608', // AFP
  '0607',                 // ONP
  '0611', '0616',         // minera trabajador
]);

function esConceptoCopiableDesdePlame(codigo) {
  return CODIGOS_COMPARTIDOS_PLAME_INTERNA.has(String(codigo || ''));
}

function esDescuentoOmitiblePlameInterna(codigo) {
  return CODIGOS_DESCUENTOS_OMITIR_PLAME_INTERNA.has(String(codigo || ''));
}

function codigoDeConcepto(item) {
  return String(
    item?.id_concepto?.codigo ||
      item?.id_concepto?.codigo_plame ||
      item?.codigo ||
      ''
  );
}

function clonarConceptoServicio(item) {
  return {
    id_concepto: item.id_concepto,
    valor: item.valor,
    ...(item.base_calculo !== undefined ? { base_calculo: item.base_calculo } : {}),
  };
}

/** Copia desde PLAME los descuentos/aportes que deben ser idénticos en ambas boletas. */
function copiarDescAportesDesdePlame(resultadoPlame, { omitirDescuentos = false } = {}) {
  const data = resultadoPlame?.data || resultadoPlame || {};
  const descuentosPlame = Array.isArray(data.dataDescuentos) ? data.dataDescuentos : [];
  const aportesPlame = Array.isArray(data.dataAportes) ? data.dataAportes : [];

  const dataDescuentos = omitirDescuentos
    ? []
    : descuentosPlame
      .filter((item) => esConceptoCopiableDesdePlame(codigoDeConcepto(item)))
      .map(clonarConceptoServicio);

  const dataAportes = aportesPlame
    .filter((item) => esConceptoCopiableDesdePlame(codigoDeConcepto(item)))
    .map(clonarConceptoServicio);

  return { dataDescuentos, dataAportes };
}

function codigoDeItemIngreso(item) {
  return String(
    item?.id_concepto?.codigo ||
      item?.id_concepto?.codigo_plame ||
      item?.codigo ||
      item?.codigo_plame ||
      item?.codigoPlame ||
      ''
  );
}

function yaExisteCodigoIngreso(lista, codigo) {
  const cod = String(codigo || '');
  if (!cod) return false;
  return (lista || []).some((i) => codigoDeItemIngreso(i) === cod);
}

const calculosBoletaInternaService = {
  /**
   * @param {object} datosCompletos - mismo shape que calculosBoletaService
   * @param {object|null} resultadoPlame - salida previa del motor PLAME (dual)
   * @param {string} origen
   */
  async generarCalculos(datosCompletos, resultadoPlame = null, origen = 'boleta_interna') {
    try {
      const { datosAPIs = {}, datosSalariales = {} } = datosCompletos || {};
      const conceptosPlame = datosAPIs.conceptosPlame || [];
      const sueldoInterno = Number(
        datosSalariales.sueldoInterno ?? datosSalariales.sueldoBasico ?? 0
      );
      const tipoContratacion = datosSalariales.tipoContratacion || 'Planilla';
      const regimenLaboral = datosSalariales.regimenLaboral || '';
      const tipoRegimen = clasificarRegimenSimple(regimenLaboral);
      const hayPlame = !!(resultadoPlame?.success || resultadoPlame?.data);
      const omitirDescuentosPlameInterna = Boolean(
        datosCompletos?.omitir_descuentos_plame_interna
        ?? datosCompletos?.datosSalariales?.omitirDescuentosPlameInterna
        ?? datosCompletos?.conceptosUsuario?.omitir_descuentos_plame_interna
      );

      let dataIngresos = [];
      let dataDescuentos = [];
      let dataAportes = [];

      // "Interno" solo existe con planilla alternativa y siempre genera boleta interna
      const TIPOS_CON_BOLETA_INTERNA = ['Planilla', 'Recibo por Honorarios', 'Interno'];
      if (!TIPOS_CON_BOLETA_INTERNA.includes(tipoContratacion)) {
        return {
          success: true,
          data: {
            mensaje: 'Boleta interna: sin conceptos para este tipo de contratación',
            origen,
            dataIngresos: [],
            dataDescuentos: [],
            dataAportes: [],
            resultadoPlame,
          },
          message: 'Boleta interna generada',
        };
      }

      // --- Ingresos sobre sueldo interno ---
      const concepto0121 =
        encontrarConceptoPlame(conceptosPlame, '0121') ||
        conceptosPlame[20] ||
        null;

      // Un solo 0121: prioriza sueldo_interno; los extras con 0121 se omiten después
      if (sueldoInterno > 0) {
        if (concepto0121) {
          dataIngresos.push({
            id_concepto: concepto0121,
            valor: sueldoInterno,
          });
        } else {
          dataIngresos.push({
            id_concepto: {
              id: 'interno-0121',
              codigo: '0121',
              codigo_plame: '0121',
              descripcion: 'REMUNERACIÓN O JORNAL BÁSICO (INTERNO)',
            },
            valor: sueldoInterno,
          });
        }
      }

      // 0201 (AF) y 0902 (bono): los flags del empleado son de PLAME.
      // En interna solo entran si están en ingresosInternos (maestro / destino explícito).

      // Ingresos adicionales del Excel / UI (boleta interna)
      const { conceptosUsuario = {} } = datosCompletos || {};
      const extrasInternos = [
        ...(conceptosUsuario.ingresosInternos || []),
        // En solo_interna a veces llegan como ingresos (ya pueden traer 0121 del array de planilla)
        ...(!hayPlame ? (conceptosUsuario.ingresos || []) : []),
      ];
      extrasInternos.forEach((ing) => {
        const codigo = String(
          ing.codigoPlame
          || ing.codigo_plame
          || ing.codigo
          || ing.plame_configuracion?.codigo_plame
          || ing.plameConfiguracion?.codigo_plame
          || ''
        );
        const monto = Number(ing.monto ?? ing.valor ?? ing.monto_interna) || 0;
        if (!codigo || monto <= 0) return;

        // Mismo código (p. ej. viáticos 0917 por día): sumar montos, no descartar
        const existente = (dataIngresos || []).find((i) => codigoDeItemIngreso(i) === codigo);
        if (existente) {
          if (codigo === '0121') return; // 0121 ya viene del sueldo interno
          existente.valor = Number(existente.valor || 0) + monto;
          return;
        }

        const concepto =
          encontrarConceptoPlame(conceptosPlame, codigo) ||
          (ing.plame_configuracion || ing.plameConfiguracion
            ? {
                id: (ing.plame_configuracion || ing.plameConfiguracion).id || ing.plame_configuracion_id || ing.id,
                codigo,
                codigo_plame: codigo,
                descripcion:
                  (ing.plame_configuracion || ing.plameConfiguracion).descripcion
                  || ing.concepto
                  || ing.descripcion
                  || ing.nombre,
              }
            : ing.id
              ? {
                  id: ing.id,
                  codigo,
                  codigo_plame: codigo,
                  descripcion: ing.concepto || ing.descripcion || ing.nombre,
                }
              : { id: `interno-${codigo}`, codigo, codigo_plame: codigo, descripcion: ing.concepto || ing.descripcion || codigo });

        if (!concepto) return;

        dataIngresos.push({
          id_concepto: {
            ...concepto,
            codigo: concepto.codigo || concepto.codigo_plame || codigo,
            codigo_plame: concepto.codigo_plame || concepto.codigo || codigo,
          },
          valor: monto,
          excluir_boleta_ordinaria: Boolean(
            ing.excluirBoletaOrdinaria || ing.excluir_boleta_ordinaria
          ),
          origen_calculo: ing.origenCalculo || ing.origen_calculo || null,
        });
      });

      // Seguridad: colapsar por código sumando valores (viáticos diarios, etc.)
      const ingresosUnicos = [];
      dataIngresos.forEach((item) => {
        const cod = codigoDeItemIngreso(item);
        if (!cod) {
          ingresosUnicos.push(item);
          return;
        }
        const prev = ingresosUnicos.find((i) => codigoDeItemIngreso(i) === cod);
        if (prev) {
          prev.valor = Number(prev.valor || 0) + Number(item.valor || 0);
          return;
        }
        ingresosUnicos.push({ ...item, valor: Number(item.valor || 0) });
      });
      dataIngresos = ingresosUnicos;

      // --- Descuentos / aportes: base PLAME (ONP/AFP, EsSalud) + operativos internos ---
      if (hayPlame) {
        const copiados = copiarDescAportesDesdePlame(resultadoPlame, {
          omitirDescuentos: omitirDescuentosPlameInterna,
        });
        dataDescuentos = copiados.dataDescuentos;
        dataAportes = copiados.dataAportes;
      }
      // solo_interna: dataDescuentos/dataAportes parten vacíos

      // Descuentos/adelantos de tareo con destino interna (sumados por código)
      const descuentosInternosExtra = Array.isArray(conceptosUsuario.descuentosInternos)
        ? conceptosUsuario.descuentosInternos
        : [];
      descuentosInternosExtra.forEach((desc) => {
        const codigo = String(
          desc.codigoPlame
          || desc.codigo_plame
          || desc.codigo
          || desc.plame_configuracion?.codigo_plame
          || desc.plameConfiguracion?.codigo_plame
          || ''
        );
        const monto = Number(desc.monto ?? desc.valor ?? desc.monto_interna) || 0;
        if (!codigo || monto <= 0) return;
        // Flag activo: no reinyectar AFP/ONP/minera desde maestro/ajustes/tareo
        if (omitirDescuentosPlameInterna && esDescuentoOmitiblePlameInterna(codigo)) return;
        // Sin flag: los compartidos ya vienen del motor PLAME; no sumar de nuevo
        if (esConceptoCopiableDesdePlame(codigo) && hayPlame) return;

        const existente = (dataDescuentos || []).find((i) => codigoDeConcepto(i) === codigo);
        if (existente) {
          existente.valor = Number(existente.valor || 0) + monto;
          return;
        }

        const concepto =
          encontrarConceptoPlame(conceptosPlame, codigo) ||
          {
            id: desc.id || `interno-desc-${codigo}`,
            codigo,
            codigo_plame: codigo,
            descripcion: desc.concepto || desc.descripcion || desc.nombre || codigo,
          };

        dataDescuentos.push({
          id_concepto: {
            ...concepto,
            codigo: concepto.codigo || concepto.codigo_plame || codigo,
            codigo_plame: concepto.codigo_plame || concepto.codigo || codigo,
          },
          valor: monto,
        });
      });

      // Colapsar descuentos por código sumando (varios adelantos/días)
      const descuentosUnicos = [];
      dataDescuentos.forEach((item) => {
        const cod = codigoDeConcepto(item);
        if (!cod) {
          descuentosUnicos.push(item);
          return;
        }
        const prev = descuentosUnicos.find((i) => codigoDeConcepto(i) === cod);
        if (prev) {
          prev.valor = Number(prev.valor || 0) + Number(item.valor || 0);
          return;
        }
        descuentosUnicos.push({ ...item, valor: Number(item.valor || 0) });
      });
      dataDescuentos = descuentosUnicos;

      return {
        success: true,
        data: {
          mensaje: hayPlame
            ? 'Boleta interna: ingresos propios + AFP/ONP/EsSalud/minera (desde PLAME) + descuentos internos'
            : 'Boleta interna: ingresos + descuentos/adelantos internos (sin aportes pensionarios)',
          origen,
          dataIngresos,
          dataDescuentos,
          dataAportes,
          resultadoPlame,
          meta: {
            sueldoInterno,
            tipoRegimen,
            copiadoDesdePlame: hayPlame,
            omitirDescuentosPlameInterna,
            sinDescuentos: omitirDescuentosPlameInterna,
            sinAportes: !hayPlame,
          },
        },
        message: 'Boleta interna generada exitosamente',
      };
    } catch (error) {
      console.error('❌ Error en calculosBoletaInternaService:', error);
      return {
        success: false,
        error: error.message || 'Error al generar boleta interna',
        data: {
          dataIngresos: [],
          dataDescuentos: [],
          dataAportes: [],
        },
      };
    }
  },
};

export default calculosBoletaInternaService;
